/**
 * CdaContractViewer — full, read-only detail of a tool's CDA contract.
 *
 * This is the "everything else" surface that lets the single GovernanceSignal
 * stay uncluttered. Generalizes the plugin-local skills-manager viewer into the
 * shared kit (Lucide-style icons, copy-to-clipboard on identifiers, the
 * destructive lock surfaced WITH its reason). Tool-field help merges the
 * skills-manager x-field dictionary with the receipt FIELD_HELP vocabulary so the
 * same explanations appear across surfaces.
 */

import type { CdaContract, CdaParam } from './types';
import { deriveGovernanceState, parseRiskLevel, isDestructive } from './types';
import { RiskLevelIndicator } from './RiskLevelIndicator';
import { GovernanceSignal } from './GovernanceSignal';
import { CdaIcon } from './icons';
import { COVERAGE_PRESENT, COVERAGE_STRONG, NEUTRAL, GAP_COLOR, softFill, softBorder, TEXT_PRIMARY, TEXT_SECONDARY, SURFACE_INSET, SURFACE_BORDER } from './colors';

const React = (globalThis as any).React;
const { useState, useCallback } = React;

const mono = '"JetBrains Mono", "Fira Code", monospace';

export interface CdaContractViewerProps {
  contract: CdaContract;
  /** Operator surfaces can assert a mutation from an authoritative binding. */
  mutatingBinding?: boolean;
  onLearnMore?: () => void;
  className?: string;
}

const X_FIELD_TOOLTIPS: Record<string, string> = {
  'x-redact': 'Collected via secure input and redacted (masked) in logs, receipts, and model context. Never persisted in plain text.',
  'x-normalize': 'Canonicalized before use (e.g. lowercased, trimmed, domain-stripped).',
  'x-suggest': 'Candidate values are suggested via a lookup; the user may disambiguate.',
  'x-default': 'Pre-filled default value (CDA inference, schema default, or training history).',
  'x-compose': 'Auto-composed infrastructure value (idempotency / correlation id). The human is never asked for it.',
  'x-elicit': 'Prompt shown when this required value is missing.',
  required: 'Required parameter — the call fails without it.',
};

function copyToClipboard(text: string): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      return navigator.clipboard.writeText(text);
    }
  } catch {
    /* ignore */
  }
  return Promise.resolve();
}

function CopyableId({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(() => {
    void copyToClipboard(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    });
  }, [value]);

  return React.createElement(
    'button',
    {
      type: 'button',
      onClick: onCopy,
      'data-testid': 'cda-copyable',
      title: `Copy ${label || 'value'}`,
      'aria-label': `Copy ${label || value}`,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: SURFACE_INSET,
        border: `1px solid ${SURFACE_BORDER}`,
        borderRadius: 'var(--border-radius-sm, 6px)',
        padding: '2px 8px',
        cursor: 'pointer',
        fontFamily: mono,
        fontSize: 11,
        color: TEXT_PRIMARY,
      },
    },
    React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 } }, value),
    React.createElement(CdaIcon, { name: copied ? 'check' : 'copy', size: 12, color: copied ? COVERAGE_STRONG : NEUTRAL }),
  );
}

function xFieldBadge(label: string, color: string) {
  return React.createElement(
    'span',
    {
      key: label,
      title: X_FIELD_TOOLTIPS[label] || label,
      style: {
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 6,
        fontWeight: 500,
        color,
        background: softFill(color, 12),
        border: `1px solid ${softBorder(color, 28)}`,
        cursor: X_FIELD_TOOLTIPS[label] ? 'help' : 'default',
      },
    },
    label,
  );
}

function section(title: string, iconName: any, children: any) {
  return React.createElement(
    'div',
    {
      style: {
        padding: '12px 14px',
        borderRadius: 'var(--card-radius, 10px)',
        border: `1px solid ${SURFACE_BORDER}`,
        background: SURFACE_INSET,
        marginBottom: 10,
      },
    },
    React.createElement(
      'div',
      { style: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY } },
      React.createElement(CdaIcon, { name: iconName, size: 14, color: TEXT_SECONDARY }),
      title,
    ),
    children,
  );
}

function renderParam(param: CdaParam) {
  return React.createElement(
    'div',
    {
      key: param.name,
      'data-testid': `cda-param-${param.name}`,
      style: {
        padding: '8px 10px',
        borderRadius: 8,
        border: `1px solid ${softBorder(NEUTRAL, 18)}`,
        marginBottom: 6,
      },
    },
    React.createElement(
      'div',
      { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
      React.createElement('code', { style: { fontWeight: 600, fontSize: 12, fontFamily: mono, color: TEXT_PRIMARY } }, param.name),
      React.createElement(
        'div',
        { style: { display: 'flex', gap: 4, flexWrap: 'wrap' } },
        param.type && xFieldBadge(param.type, NEUTRAL),
        param.required && xFieldBadge('required', 'var(--color-danger, #ef4444)'),
        param['x-redact'] && xFieldBadge('x-redact', 'var(--color-danger, #dc2626)'),
        param['x-normalize'] && xFieldBadge('x-normalize', COVERAGE_PRESENT),
        param['x-suggest'] !== undefined && param['x-suggest'] !== null && xFieldBadge('x-suggest', 'var(--color-purple, #8b5cf6)'),
        param['x-default'] !== undefined && xFieldBadge('x-default', COVERAGE_STRONG),
        param['x-compose'] && xFieldBadge('x-compose', NEUTRAL),
        param['x-elicit'] && xFieldBadge('x-elicit', COVERAGE_PRESENT),
      ),
    ),
    param.description &&
      React.createElement('div', { style: { fontSize: 11, color: TEXT_SECONDARY, marginTop: 4, lineHeight: 1.4 } }, param.description),
  );
}

export function CdaContractViewer({ contract, mutatingBinding, onLearnMore, className }: CdaContractViewerProps) {
  const info = deriveGovernanceState(contract, { mutatingBinding });
  const risk = parseRiskLevel(contract['x-risk']);
  const scope = contract['x-confirm-scope'];
  const destructive = isDestructive(contract);

  return React.createElement(
    'div',
    { 'data-testid': 'cda-contract-viewer', 'data-governance-state': info.state, className: className || undefined },

    // Header: one primary signal + inherent-risk channel
    React.createElement(
      'div',
      { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 } },
      React.createElement(GovernanceSignal, { contract, mutatingBinding, hideRisk: true }),
      risk && React.createElement(RiskLevelIndicator, { level: risk, rawLabel: contract['x-risk'] }),
      contract['x-discloses-value'] &&
        React.createElement(
          'span',
          {
            title: 'Returns a secret value — disclosed via a secure, client-bound reveal frame and withheld from the model.',
            style: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: COVERAGE_PRESENT },
          },
          React.createElement(CdaIcon, { name: 'eye', size: 13, color: COVERAGE_PRESENT }),
          'Discloses value',
        ),
      onLearnMore &&
        React.createElement(
          'button',
          {
            onClick: onLearnMore,
            'data-testid': 'cda-viewer-learn-more',
            style: { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: COVERAGE_PRESENT, fontFamily: 'inherit' },
          },
          'Learn more',
          React.createElement(CdaIcon, { name: 'external-link', size: 11, color: COVERAGE_PRESENT }),
        ),
    ),

    // Read-only calm state (no contract, not a finding)
    info.state === 'read-only' &&
      React.createElement(
        'div',
        {
          'data-testid': 'cda-state-readonly',
          style: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, color: NEUTRAL, fontSize: 12, background: softFill(NEUTRAL, 8) },
        },
        React.createElement(CdaIcon, { name: 'eye', size: 14, color: NEUTRAL }),
        'Read-only tool — no mutation governance needed.',
      ),

    // Governance-gap finding (NOT a comforting empty state)
    info.state === 'gap' &&
      React.createElement(
        'div',
        {
          'data-testid': 'cda-state-gap',
          role: 'alert',
          style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, color: GAP_COLOR, fontSize: 12, background: softFill(GAP_COLOR, 14), border: `1px solid ${softBorder(GAP_COLOR, 40)}`, marginBottom: 10 },
        },
        React.createElement(CdaIcon, { name: 'shield-alert', size: 16, color: GAP_COLOR }),
        React.createElement(
          'span',
          null,
          React.createElement('strong', null, 'Governance gap. '),
          'This tool mutates state but declares no Contract-Driven Autonomy contract (no x-cda, confirmation, or impact). Add a CDA contract before relying on it.',
        ),
      ),

    // Impact
    contract['x-impact'] &&
      section(
        'Impact',
        contract['x-impact'].type === 'delete' ? 'alert-triangle' : 'info',
        React.createElement(
          'div',
          { style: { fontSize: 12, color: TEXT_SECONDARY, lineHeight: 1.5 } },
          contract['x-impact'].type &&
            React.createElement('span', { style: { fontWeight: 600, color: destructive ? 'var(--color-danger, #ef4444)' : TEXT_PRIMARY, marginRight: 6 } }, contract['x-impact'].type),
          contract['x-impact'].description || '',
        ),
      ),

    // Confirmation message
    contract['x-confirm-message'] &&
      section(
        'Confirmation message',
        'shield-check',
        React.createElement('div', { style: { fontSize: 12, color: TEXT_SECONDARY, lineHeight: 1.5, fontStyle: 'italic' } }, `"${contract['x-confirm-message']}"`),
      ),

    // Batch consent scope + destructive lock
    scope &&
      section(
        'Batch consent',
        destructive ? 'lock' : 'shield-check',
        React.createElement(
          'div',
          { style: { fontSize: 12, color: TEXT_SECONDARY } },
          React.createElement(
            'div',
            { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: destructive ? 8 : 0 } },
            scope.grant && xFieldBadge(scope.grant, COVERAGE_PRESENT),
            scope.required_amr && scope.required_amr.length > 0 && xFieldBadge(`step-up: ${scope.required_amr.join(',')}`, COVERAGE_STRONG),
            typeof scope.max_candidates === 'number' && xFieldBadge(`≤ ${scope.max_candidates} candidates`, NEUTRAL),
            typeof scope.max_operations === 'number' && xFieldBadge(`≤ ${scope.max_operations} ops`, NEUTRAL),
            typeof scope.ttl_seconds === 'number' && xFieldBadge(`ttl ${scope.ttl_seconds}s`, NEUTRAL),
          ),
          destructive &&
            React.createElement(
              'div',
              {
                'data-testid': 'cda-destructive-lock',
                style: { display: 'flex', alignItems: 'flex-start', gap: 6, padding: '8px 10px', borderRadius: 8, color: 'var(--color-danger, #ef4444)', background: softFill('var(--color-danger, #ef4444)', 10), fontSize: 11.5, lineHeight: 1.45 },
              },
              React.createElement(CdaIcon, { name: 'lock', size: 13, color: 'var(--color-danger, #ef4444)' }),
              React.createElement(
                'span',
                null,
                React.createElement('strong', null, 'Destructive guardrail (TOOL-22): '),
                'per-tool-list consent, no standing grant, and step-up MFA at mint — enforced because the impact is irreversible.',
              ),
            ),
        ),
      ),

    // Operation ref (copyable)
    contract['x-operation-ref'] &&
      section(
        'PDP operation',
        'shield-check',
        React.createElement(CopyableId, { value: contract['x-operation-ref'], label: 'operation ref' }),
      ),

    // Parameters
    contract.params.length > 0 &&
      section(
        `Parameters (${contract.params.length})`,
        'circle-dot',
        React.createElement('div', null, ...contract.params.map(renderParam)),
      ),
  );
}
